import React from 'react';
import Grid from '@mui/material/Grid2';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';
import { FilterRowSelection } from '@lambda-layer/types/filter.ts';

interface Props {
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowValue: FilterRowSelection) => void;
  onFocusChange?: () => void;
  isRange?: boolean;
  autoFocus?: boolean;
}

const FilterRowDate: React.FC<Props> = ({
  filterRowSelection,
  onChange,
  onFocusChange,
  isRange = false,
  autoFocus = false,
}) => {
  // parse "YYYY-MM-DD" as local midnight
  const parseLocalDate = (iso: string) => {
    // Remove the time part if present
    const [year, month, day] = iso.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // normalize an incoming value (string or Date) -> local Date
  const toDate = (v: string | Date | null): Date | null => {
    if (v == null) return null;
    return v instanceof Date ? v : parseLocalDate(v);
  };

  const handleDateChange = (date: Date | null, type: 'start' | 'end') => {
    const fmt = (d: Date | null) => (d ? format(d, 'yyyy-MM-dd') : null);

    let updatedValue: FilterRowSelection;

    if (!isRange) {
      updatedValue = {
        ...filterRowSelection,
        value: date ? [fmt(date)!] : [],
      };
    } else {
      const existing0 = filterRowSelection.value[0] as string | Date | null;
      const existing1 = filterRowSelection.value[1] as string | Date | null;

      const start =
        type === 'start'
          ? fmt(date)
          : existing0
            ? fmt(
                existing0 instanceof Date
                  ? existing0
                  : parseLocalDate(existing0)
              )
            : null;
      const end =
        type === 'end'
          ? fmt(date)
          : existing1
            ? fmt(
                existing1 instanceof Date
                  ? existing1
                  : parseLocalDate(existing1)
              )
            : null;

      updatedValue = {
        ...filterRowSelection,
        value: start === null && end === null ? [] : [start, end],
      };
    }

    if (onFocusChange) {
      onFocusChange();
    }
    onChange(updatedValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {!isRange ? (
        <DatePicker
          autoFocus={autoFocus}
          label="Date"
          value={toDate(filterRowSelection.value[0] as string | Date | null)}
          onChange={date => handleDateChange(date, 'start')}
          slotProps={{
            textField: { size: 'small' },
            field: { clearable: true },
          }}
        />
      ) : (
        <Grid container spacing={1}>
          <Grid size={6}>
            <DatePicker
              label="Start Date"
              value={toDate(
                filterRowSelection.value[0] as string | Date | null
              )}
              onChange={date => handleDateChange(date, 'start')}
              slotProps={{
                textField: { size: 'small' },
                field: { clearable: true },
              }}
            />
          </Grid>
          <Grid size={6}>
            <DatePicker
              label="End Date"
              value={toDate(
                filterRowSelection.value[1] as string | Date | null
              )}
              onChange={date => handleDateChange(date, 'end')}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Grid>
        </Grid>
      )}
    </LocalizationProvider>
  );
};

export default FilterRowDate;
